import { z } from "zod";

const lineupEntrySchema = z.object({
  player_id: z.string().min(1, "Player is required"),
  jersey_number: z.number().int().min(1, "Jersey number must be at least 1"),
  is_captain: z.boolean().default(false),
  is_vice_captain: z.boolean().default(false),
  covers_positions: z.array(z.number().int().min(1).max(15)).default([]),
  // 153: BokSmart front-row backup designation (reserves only)
  backup_hooker: z.boolean().default(false),
  backup_prop: z.boolean().default(false),
});

export const upsertLineupSchema = z
  .object({
    lineup: z.array(lineupEntrySchema).min(1, "At least one player is required"),
  })
  .refine(
    (data) => {
      const captains = data.lineup.filter((e) => e.is_captain);
      return captains.length <= 1;
    },
    { message: "Only one captain allowed", path: ["lineup"] },
  )
  .refine(
    (data) => {
      const vcs = data.lineup.filter((e) => e.is_vice_captain);
      return vcs.length <= 1;
    },
    { message: "Only one vice-captain allowed", path: ["lineup"] },
  )
  .refine(
    (data) => {
      const captain = data.lineup.find((e) => e.is_captain);
      const vc = data.lineup.find((e) => e.is_vice_captain);
      if (captain && vc) return captain.player_id !== vc.player_id;
      return true;
    },
    { message: "Captain and vice-captain must be different players", path: ["lineup"] },
  )
  .refine(
    (data) => {
      const jerseyNumbers = data.lineup.map((e) => e.jersey_number);
      return new Set(jerseyNumbers).size === jerseyNumbers.length;
    },
    { message: "Duplicate jersey numbers not allowed", path: ["lineup"] },
  )
  .refine(
    (data) => {
      const playerIds = data.lineup.map((e) => e.player_id);
      return new Set(playerIds).size === playerIds.length;
    },
    { message: "Duplicate players not allowed", path: ["lineup"] },
  )
  .refine(
    (data) => {
      // Only reserves (jersey > 15) can have covers_positions
      return data.lineup.every(
        (e) => e.jersey_number > 15 || e.covers_positions.length === 0,
      );
    },
    { message: "Only reserves can have covers_positions", path: ["lineup"] },
  )
  .refine(
    (data) => {
      // 153: only reserves (jersey > 15) can be designated backup hooker/prop.
      // Presence of a backup set is NOT required here (advisory/soft per spec) --
      // this only constrains where the flag may be placed.
      return data.lineup.every(
        (e) => e.jersey_number > 15 || (!e.backup_hooker && !e.backup_prop),
      );
    },
    { message: "Only reserves can be designated backup hooker/prop", path: ["lineup"] },
  );

export type LineupEntryInput = z.infer<typeof lineupEntrySchema>;
export type UpsertLineupInput = z.infer<typeof upsertLineupSchema>;

export const setPlayerPositionSchema = z
  .object({
    jersey_number: z.number().int().min(1, "Jersey number must be at least 1"),
    is_captain: z.boolean().default(false),
    is_vice_captain: z.boolean().default(false),
    secondary_positions: z.array(z.number().int().min(1)).default([]),
  })
  .refine((d) => !(d.is_captain && d.is_vice_captain), {
    message: "Cannot be both captain and vice-captain",
  });

export type SetPlayerPositionSchemaInput = z.infer<
  typeof setPlayerPositionSchema
>;
